import { BdsButton, BdsPaper, BdsTypo } from 'blip-ds/dist/blip-ds-react';
import React, { FC, useContext, useEffect } from 'react';
import { startLoading, stopLoading } from '../../../api/CommonApi';
import { BlipTable } from '../../../components/BlipTable';
import DailyEngagedUsersContext from '../../../contexts/DailyEngagedUsersContext';
import { useCommon } from '../../../contexts/CommonContext';
import { getMonthTwoDigits, toUTCDateString } from '../../../utils/date';
import {
  DailyEngagedUserRow,
  DailyEngagedUserPaper,
  DailyEngagedUserUsageStatementContent,
  DailyEngagedUserConsumptionConsolidatedHeaderPartial,
  DailyEngagedUserConsumptionConsolidatedHeaderRow,
} from '../styles';
import { DateDropdown } from '../../../components/DateDropdown';
import { Link } from 'react-router-dom';
import {
  DAILY_ACTIVE_USERS_DAILY_BALANCE_STATEMENT_DETAIL_CLICK,
  DAILY_ACTIVE_USERS_PERIOD_SELECT_DAILY_OPENED,
} from '../../../shared/SegmentEvents';
import { createTrack } from '../../../api/SegmentApi';
import FeatureToggleService from '../../../services/FeatureToggleService';
import { BillingMetrics } from '../../../shared/BillingMetrics';

export interface DailyEngagedUserUsageProps {
  registersToTake: number;
  tableBodyHeight: string;
}

const DailyEngagedUserUsageComponent: FC<DailyEngagedUserUsageProps> = ({
  registersToTake = -1,
  tableBodyHeight = '200px',
}) => {
  const {
    currentMonth,
    dateToSearch,
    content,
    tenant,
    loggedUser,
    sortDailyEngagedUserUsage,
    setDailyEngagedUserUsage,
    dailyEngagedUserUsage,
    billingDailyEngagedUserApi,
    periodOptions,
    setDateToSearch,
  } = useContext(DailyEngagedUsersContext);
  const { setHeaderContent } = useCommon();

  const tableDailyUsageModelDeu = [
    { label: content.dailyUsage.byDaily.date, key: 'dateReference' },
    { label: content.dailyUsage.byDaily.dailyEngagedUsers, key: 'total' },
  ];

  useEffect(() => {
    const getDataDeu = async () => {
      startLoading();
      const storageType = await FeatureToggleService.isDatabricksStorageTypeEnabled(BillingMetrics.dailyEngagedUser) ? 1 : 0;

      const monthStringDeu = `${dateToSearch.getFullYear()}-${getMonthTwoDigits(dateToSearch)}`;
      const dailyEngagedUserUsageDeu = await billingDailyEngagedUserApi.getDailyActiveUserUsage(
        tenant.info.id,
        monthStringDeu,
        registersToTake,
        storageType,
      );

      const dailyEngagedUserUsageParseDeu = dailyEngagedUserUsageDeu.map((obj) => ({
        ...obj,
        dateReference: toUTCDateString(obj.dateReference, content.locale),
      }));

      setDailyEngagedUserUsage(dailyEngagedUserUsageParseDeu);

      stopLoading();
    };

    getDataDeu();

    return () => setHeaderContent({ redirect: null });
  }, [dateToSearch]);

  const trackDauDailyBalanceStatementDetailDeu = async () => {
    createTrack(tenant.info, DAILY_ACTIVE_USERS_DAILY_BALANCE_STATEMENT_DETAIL_CLICK, {
      dauDate: dateToSearch.getFullYear() + '-' + ('0' + (dateToSearch.getMonth() + 1)).slice(-2),
      dauUser: loggedUser.email,
    });
  };

  const periodOptionChangeEventDeu = async (selectedOption) => {
    const dateToRetrieveData = new Date(selectedOption.detail.value);
    setDateToSearch(dateToRetrieveData);
    createTrack(tenant.info, DAILY_ACTIVE_USERS_PERIOD_SELECT_DAILY_OPENED, {
      dauDate: dateToRetrieveData.getFullYear() + '-' + ('0' + (dateToRetrieveData.getMonth() + 1)).slice(-2),
      dauUser: loggedUser.email,
    });
  };

  return (
    <DailyEngagedUserPaper>
      <DailyEngagedUserRow>
        <BdsTypo className="title" tag="h3" variant="fs-24" bold="bold" margin={false}>
          {content.dailyUsage.title}
          <BdsTypo variant="fs-14">{content.dailyUsage.tableInformation}</BdsTypo>
        </BdsTypo>
        {registersToTake > 0 ? (
          <Link to={{ pathname: '/daily-active-users/daily', state: { DailyEngagedUserDaily: true } }}>
            <BdsButton variant="ghost" arrow={true} size="short" onClick={trackDauDailyBalanceStatementDetailDeu}>
              {content.dailyUsage.buttonTitle}
            </BdsButton>
          </Link>
        ) : (
          <>
            <DailyEngagedUserConsumptionConsolidatedHeaderPartial>
              <DailyEngagedUserConsumptionConsolidatedHeaderRow>
                {currentMonth && (
                  <BdsPaper className="partialUsage" elevation="static">
                    <BdsTypo variant="fs-12" bold="semi-bold">
                      {content.compiledDailyEngagedUserInformation.partialUsage}
                    </BdsTypo>
                  </BdsPaper>
                )}
              </DailyEngagedUserConsumptionConsolidatedHeaderRow>
            </DailyEngagedUserConsumptionConsolidatedHeaderPartial>
            <DateDropdown
              value={dateToSearch.toISOString()}
              bdsChangeEvent={periodOptionChangeEventDeu}
              options={periodOptions}
              label={content.monthPlaceholder}
            ></DateDropdown>
          </>
        )}
      </DailyEngagedUserRow>
      <DailyEngagedUserUsageStatementContent>
        <BdsPaper>
          <BlipTable
            idKey="date"
            model={tableDailyUsageModelDeu}
            data={dailyEngagedUserUsage}
            selectedItems={[]}
            onSortSet={sortDailyEngagedUserUsage}
            canSelect={false}
            bodyHeight={tableBodyHeight}
          ></BlipTable>
        </BdsPaper>
      </DailyEngagedUserUsageStatementContent>
    </DailyEngagedUserPaper>
  );
};

export default DailyEngagedUserUsageComponent;
